import {FormState, FormStateWithIssues} from '@raison/form-models';
import {
    createMoney,
    formatCurrency,
    fromAccountBalance,
    fromMoney,
    NonNegativeDoubleString,
    TransactionId,
} from '@raison/types';
import React, {useMemo, useReducer} from 'react';
import {locale} from '~/config';
import {RsnAmount, RsnAmountInput} from '~/library/ui-kit';
import {BareAccount} from '../../../model/Scheme/BareAccount';
import {ExchangeSettings} from '../../../model/Scheme/ExchangeSettings';
import {usePreview} from './useExchangeForm/usePreview';
import {useSubmit} from './useExchangeForm/useSubmit';

interface UseFormProps {
    readonly fromAccount: BareAccount;
    readonly toAccount: BareAccount;
    readonly settings: ExchangeSettings;
    readonly onSuccess: (id: TransactionId) => void;
}

type ExchangeFormState = ReturnType<typeof ExchangeFormState>;
const ExchangeFormState = () =>
    FormState.from({
        amount: RsnAmount.empty(),
    });

type Values = FormState.inferValues<ExchangeFormState>;

export const useExchangeForm = ({fromAccount, toAccount, settings, onSuccess}: UseFormProps) => {
    const [form, dispatchForm] = useReducer(FormState.reduce<Values>, null, ExchangeFormState);

    // RsnAmount allows intermediate states like "" and ".", BigNumber normalizes them to 0
    const enteredAmount = RsnAmount.toBigNumber(form.fields.amount.value).toString();
    const inputMoney = useMemo(
        () => createMoney(fromAccount.currency, fromAccount.decimals, enteredAmount as NonNegativeDoubleString),
        [fromAccount.currency, fromAccount.decimals, enteredAmount]
    );

    const minimalMoney = createMoney(fromAccount.currency, fromAccount.decimals, settings.minimal);
    const balanceMoney = fromAccountBalance(fromAccount);

    const t = {
        minHint: `Min: ${formatCurrency(fromMoney(minimalMoney, locale))}`,
        notEnoughFunds: 'Not enough funds',
    };

    const formWithIssues = FormStateWithIssues.fromRules(form, {
        amount: [
            RsnAmount.validateMin(settings.minimal, t.minHint),
            RsnAmount.validateMax(balanceMoney.amount, t.notEnoughFunds),
        ],
    });

    const {renderPreview, resultMoney} = usePreview({fromAccount, toAccount, settings, inputMoney});

    const {submit, renderServerErrorModal} = useSubmit({inputMoney, onSuccess});

    const {fields, isSubmitDisabled, onSubmit} = FormState.selectFormView(formWithIssues, {
        dispatch: dispatchForm,
        submit,
    });

    const renderHint = () => t.minHint;
    const renderHintAction = () => <RsnAmountInput.HintActionUseAll all={balanceMoney.amount} />;

    return {
        amountField: fields.amount,
        amountInputOptions: {
            suffix: fromAccount.currency,
            maxIntegers: 10,
            maxDecimals: fromAccount.decimals,
        },
        onSubmit,
        isSubmitDisabled,
        renderHint,
        renderHintAction,
        renderPreview,
        renderServerErrorModal,
        receiveAmount: resultMoney.amount,
    };
};
